import React, { Component, useEffect, useState } from 'react';
import { array, arrayOf, bool, func, number, object, shape, string } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm, FormSpy } from 'react-final-form';
import classNames from 'classnames';

import { propTypes } from '../../../util/types';
import {
  isFirstBookingDiscountEligible,
  isFirstBookingDiscountFlagOn,
} from '../../../util/firstBookingDiscount';
import { formatMoney } from '../../../util/currency';
import { applyHourlyCustomerPriceSubunits } from '../../../util/cocopoolPricing';
import { isBookingBelowMinAdvanceHours, parseMinAdvanceHours } from '../../../util/minAdvanceHours';
import { isBookingBelowMinPrice } from '../../../util/minBookingPrice';
import {
  isGuestTier51PlusKey,
  resolvePriceRangeRowForGuestRange,
} from '../../../util/capacityFieldUtils';
import {
  timestampToDate,
  getStartOf,
  parseDateFromISO8601,
  monthIdString,
  findNextBoundary,
  stringifyDateToISO8601,
} from '../../../util/dates';
import {
  getMonthlyTimeSlots,
  getTimeSlots,
  getAvailableStartTimes,
  getAvailableEndTimes,
  getAllTimeValues,
} from './FieldDateAndTimeInput';
import { types as sdkTypes } from '../../../util/sdkLoader';
import { FormattedMessage, intlShape, injectIntl } from '../../../util/reactIntl';
import {
  composeValidators,
  maxLength,
  minLength,
  numberAtLeast,
  numberAtMax,
  required,
} from '../../../util/validators';
import { BOOKING_PROCESS_NAME } from '../../../transactions/transaction';

import {
  FieldCheckbox,
  FieldSelect,
  Form,
  H1,
  H6,
  Modal,
  PrimaryButton,
  SecondaryButton,
} from '../../../components';

import EstimatedCustomerBreakdownMaybe from '../EstimatedCustomerBreakdownMaybe';
import ListingReservationBreakdown from '../ListingReservationBreakdown';
import FieldDateAndTimeInput from './FieldDateAndTimeInput';

import { openAccessLoginModal } from '../../../util/accessLoginModal';
import { parseListingBookingPrefill } from '../../../util/listingBookingPrefill';
import {
  gardenBookingExcludesPool,
  offersPoolUseExtra,
  resolveListingFace,
  faceView,
} from '../../../util/listingFaces';
import { extraIndoorSpacesOf } from '../../../util/indoorSpaces';

import css from './BookingTimeForm.module.css';
import InquiryForm from '../../../containers/ListingPage/InquiryForm/InquiryForm';

const { Money } = sdkTypes;
const MAX_MOBILE_SCREEN_WIDTH = 1024;

const matchTimeHm = (optHm, wantHm) => {
  if (!wantHm || optHm == null) {
    return false;
  }
  const norm = s => {
    const [h, m] = String(s)
      .split(':')
      .map(x => parseInt(x, 10));
    if (Number.isNaN(h)) {
      return String(s);
    }
    return `${String(h).padStart(2, '0')}:${String(m || 0).padStart(2, '0')}`;
  };
  const a = norm(optHm);
  let b = norm(wantHm);
  if (b === '24:00') {
    b = '00:00';
  }
  return a === b;
};

const hmToMinutes = hm => {
  if (!hm) return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hm).trim());
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  if (!Number.isFinite(h) || !Number.isFinite(min)) return null;
  // Treat 24:00 as end-of-day.
  if (h === 24 && min === 0) h = 24;
  return h * 60 + min;
};

const pickClosestTimeOption = (opts, wantHm) => {
  const want = hmToMinutes(wantHm === '24:00' ? '24:00' : wantHm);
  if (want == null || !Array.isArray(opts) || opts.length === 0) return null;
  let best = null;
  let bestDelta = Infinity;
  for (const o of opts) {
    const cur = hmToMinutes(
      o?.timeOfDay === '00:00' && wantHm === '24:00' ? '24:00' : o?.timeOfDay
    );
    if (cur == null) continue;
    const d = Math.abs(cur - want);
    if (d < bestDelta) {
      bestDelta = d;
      best = o;
    }
  }
  return best;
};

// Time-slot queries must start now or later: a month-start in the past (e.g. prefilling a
// date later this month) returns no usable slots. Clamp the range start to the next bookable
// boundary, mirroring the loadData fetch on the ListingPage.
const monthTimeSlotsRange = (startDay, timeZone) => {
  const monthStart = getStartOf(startDay, 'month', timeZone);
  const nextBoundary = findNextBoundary(new Date(), 'hour', timeZone);
  const rangeStart = monthStart.getTime() < nextBoundary.getTime() ? nextBoundary : monthStart;
  const rangeEnd = getStartOf(monthStart, 'month', timeZone, 1, 'months');
  return { rangeStart, rangeEnd };
};

/**
 * Aplica pub_capacity + dates + time de la URL cuando ya hay monthlyTimeSlots del mes.
 */
export const buildBookingPrefillFormPatch = (
  prefill,
  { intl, timeZone, monthlyTimeSlots, minimumBookingHours }
) => {
  if (!prefill || !timeZone || !intl || !monthlyTimeSlots) {
    return null;
  }
  const patch = {};
  if (prefill.guestCount) {
    patch.guestCount = prefill.guestCount;
  }
  if (!prefill.startDateYmd) {
    return Object.keys(patch).length ? patch : null;
  }

  const startDay = getStartOf(
    parseDateFromISO8601(prefill.startDateYmd, '12:00', timeZone),
    'day',
    timeZone
  );
  const monthKey = monthIdString(startDay, timeZone);
  if (!monthlyTimeSlots[monthKey]?.timeSlots?.length) {
    return Object.keys(patch).length ? patch : null;
  }

  const timeSlotsMonth = getMonthlyTimeSlots(
    monthlyTimeSlots,
    getStartOf(startDay, 'month', timeZone),
    timeZone
  );
  const onDate = getTimeSlots(timeSlotsMonth, startDay, timeZone);
  if (!onDate.length) {
    return Object.keys(patch).length ? patch : null;
  }

  const availStart = getAvailableStartTimes(intl, timeZone, startDay, onDate);
  let startTs = null;
  if (prefill.startTimeHm && availStart.length) {
    const found =
      availStart.find(t => matchTimeHm(t.timeOfDay, prefill.startTimeHm)) ||
      pickClosestTimeOption(availStart, prefill.startTimeHm);
    startTs = found ? String(found.timestamp) : null;
  }
  if (!startTs && availStart.length) {
    startTs = String(availStart[0].timestamp);
  }
  if (!startTs) {
    return Object.keys(patch).length ? patch : null;
  }

  // Hourly bookings in Cocopool must remain on the selected start day.
  // Ignore URL end date hints to avoid prefilled multi-day spans.
  const endDayHint = startDay;

  const { endDate, endTime, selectedTimeSlot } = getAllTimeValues(
    intl,
    timeZone,
    onDate,
    startDay,
    startTs,
    endDayHint,
    minimumBookingHours
  );

  let endTs = endTime;
  if (prefill.endTimeHm && selectedTimeSlot) {
    const endAvail = getAvailableEndTimes(intl, timeZone, startTs, endDate, selectedTimeSlot);
    const foundEnd =
      endAvail.find(t => {
        const hod = t.timeOfDay === '00:00' ? '24:00' : t.timeOfDay;
        return matchTimeHm(hod, prefill.endTimeHm) || matchTimeHm(t.timeOfDay, prefill.endTimeHm);
      }) || pickClosestTimeOption(endAvail, prefill.endTimeHm);
    if (foundEnd) {
      endTs = String(foundEnd.timestamp);
    }
  }

  return {
    ...patch,
    bookingStartDate: { date: startDay },
    bookingStartTime: startTs,
    bookingEndDate: { date: endDate },
    bookingEndTime: endTs,
  };
};

/**
 * Tramos de precio con los que se está reservando. `facePriceRange` llega resuelto
 * desde la ficha cuando el anuncio se está mirando por su cara de jardín; sin él, los
 * del anuncio, que son los de siempre (ver src/util/listingFaces.js).
 */
const facePriceRangeOf = props =>
  Array.isArray(props?.facePriceRange) && props.facePriceRange.length > 0
    ? props.facePriceRange
    : props?.listing?.attributes?.publicData?.priceRange;

const guestPackageDisplayLabel = (priceRange, guestRange, intl, unitType, unitPrice) => {
  if (!Array.isArray(priceRange) || !guestRange) {
    return null;
  }
  const row = priceRange.find(r => String(r?.quantityRange) === String(guestRange));
  if (!row) {
    return null;
  }
  let quantityText = '';
  if (isGuestTier51PlusKey(row.quantityRange)) {
    quantityText = intl.formatMessage({ id: 'ListingReservationBreakdown.guests51Plus' });
  } else if (row.quantityRange.split('-').length > 1) {
    const [a, b] = row.quantityRange.split('-');
    quantityText = intl.formatMessage(
      { id: 'ListingReservationBreakdown.guestsBetween' },
      { min: a, max: b }
    );
  } else {
    quantityText = intl.formatMessage(
      { id: 'ListingReservationBreakdown.guestsExact' },
      { n: row.quantityRange }
    );
  }
  const displayAmount =
    unitType === 'hour' ? applyHourlyCustomerPriceSubunits(row.amount) : row.amount;
  const displayMoney = new Money(displayAmount, row.currency || unitPrice?.currency);
  return `${quantityText} ${formatMoney(intl, displayMoney)} /h`;
};

// El prefill de la URL se escribe en el formulario desde aquí y no desde los métodos del
// ciclo de vida: `useField` (react-final-form 6.5) se suscribe en un efecto e ignora la primera
// notificación, así que un `change` hecho antes de ese efecto llega al formulario pero no al
// campo, que sigue pintando y enviando otra cosa. Pintado después de los campos, este efecto
// corre después de los suyos.
const AfterFieldsSubscribed = ({ onCommit }) => {
  useEffect(() => {
    onCommit();
  });
  return null;
};

export class BookingTimeFormComponent extends Component {
  constructor(props) {
    super(props);

    this.handleFormSubmit = this.handleFormSubmit.bind(this);
    this.handleOnChange = this.handleOnChange.bind(this);
    this.getEffectivePrefill = this.getEffectivePrefill.bind(this);
    this.tryApplyDateTimePrefill = this.tryApplyDateTimePrefill.bind(this);
    this.prefillApplied = false;
    this.prefillMonthKeyRequested = null;
    /** Evita spam a /api/transaction-line-items: FormSpy dispara en cada actualización de valores. */
    this.lastLineItemsRequestKey = null;
    this.formRef = null;
    this.applyPrefillAfterCommit = this.applyPrefillAfterCommit.bind(this);
    this.getInitialValues = this.getInitialValues.bind(this);
    this.initialValuesKey = null;
    this.initialValuesCache = undefined;
    this.state = {
      showContent: false,
      selectedOption: null,
    };
    this.myRef = React.createRef();
  }

  // Build the FinalForm initialValues from an explicit prefill (TransactionPage inquiry),
  // so date/guest/extras render selected from the first paint instead of via async change().
  // Cached by a stable key so FinalForm doesn't reinitialize (and wipe edits) on every render.
  getInitialValues() {
    const prefill = this.props.bookingInitialPrefill;
    const { timeZone, extraItems: listingExtras } = this.props;
    const storedExtraNames =
      prefill && Array.isArray(prefill.extraItems)
        ? prefill.extraItems.map(i => i?.itemName)
        : null;
    const key = prefill
      ? JSON.stringify({
          g: prefill.guestCount,
          d: prefill.startDateYmd,
          s: prefill.bookingStartTime,
          e: prefill.bookingEndTime,
          x: storedExtraNames,
          p: prefill.poolUseExtra,
        })
      : '';
    if (this.initialValuesKey === key) {
      return this.initialValuesCache;
    }
    this.initialValuesKey = key;

    let initialValues;
    if (prefill) {
      const iv = {};
      if (prefill.guestCount) {
        iv.guestCount = prefill.guestCount;
      }
      if (prefill.startDateYmd && timeZone) {
        const startDay = getStartOf(
          parseDateFromISO8601(prefill.startDateYmd, '12:00', timeZone),
          'day',
          timeZone
        );
        iv.bookingStartDate = { date: startDay };
        iv.bookingEndDate = { date: startDay };
      }
      // Exact timestamps from the inquiry match the generated time-select options once the
      // month's slots load, so the selects show the right times without an extra interaction.
      if (prefill.bookingStartTime) {
        iv.bookingStartTime = String(prefill.bookingStartTime);
      }
      if (prefill.bookingEndTime) {
        iv.bookingEndTime = String(prefill.bookingEndTime);
      }
      // Checkboxes compare by object reference, so reuse the listing's extra-item objects
      // (matched by name) rather than the stored copies.
      if (storedExtraNames && storedExtraNames.length && Array.isArray(listingExtras)) {
        const names = new Set(storedExtraNames);
        const matched = listingExtras.filter(i => names.has(i?.itemName));
        if (matched.length) {
          iv.extraItems = matched;
        }
      }
      if (prefill.poolUseExtra === true) {
        iv.poolUseExtra = true;
      }
      initialValues = Object.keys(iv).length ? iv : undefined;
    }
    // Espacios interiores ya añadidos desde la ficha: en móvil el formulario se monta al abrir
    // el modal y tiene que llegar con ellos marcados. No entran en la clave de la caché: si la
    // selección cambia con el formulario montado, la lleva syncIndoorSpaceExtrasFromProps.
    const { indoorSpaceExtras } = this.props;
    if (Array.isArray(indoorSpaceExtras) && indoorSpaceExtras.length) {
      initialValues = { ...initialValues, indoorSpaceExtras };
    }
    this.initialValuesCache = initialValues;
    return initialValues;
  }

  getEffectivePrefill(props = this.props) {
    const search =
      (typeof window !== 'undefined' &&
      typeof window.location?.search === 'string' &&
      window.location.search
        ? window.location.search
        : null) ||
      (props.location && typeof props.location.search === 'string' && props.location.search) ||
      '';
    const fromUrl = parseListingBookingPrefill(search);
    const fromProps = props.bookingSearchPrefill;
    const hasProps = fromProps && Object.keys(fromProps).length > 0;
    const hasUrl = fromUrl && Object.keys(fromUrl).length > 0;
    if (!hasProps && !hasUrl) {
      return null;
    }
    // URL wins so a partial/stale value from an earlier parent render cannot block full prefill.
    const prefill = { ...(hasProps ? fromProps : {}), ...(hasUrl ? fromUrl : {}) };

    // `pub_capacity` es un bucket global del buscador (`1-5`), no necesariamente un tramo de este
    // anuncio (que puede tener `1-20`). Se traduce al tramo real antes de tocar el formulario: un
    // `guestCount` que el anuncio no reconoce no seleccionaría nada y llegaría así al cálculo de
    // line items.
    if (prefill.guestCount) {
      const row = resolvePriceRangeRowForGuestRange(facePriceRangeOf(props), prefill.guestCount);
      if (row) {
        prefill.guestCount = row.quantityRange;
      } else {
        delete prefill.guestCount;
      }
    }

    return Object.keys(prefill).length ? prefill : null;
  }

  componentDidMount() {
    const {
      listing,
      intl,
      price,
      listingId,
      onFetchTimeSlots,
      timeZone,
      monthlyTimeSlots,
    } = this.props;
    const bookingSearchPrefill = this.getEffectivePrefill();
    if (bookingSearchPrefill?.guestCount && listing) {
      const pd = listing?.attributes?.publicData;
      const label = guestPackageDisplayLabel(
        facePriceRangeOf(this.props),
        bookingSearchPrefill.guestCount,
        intl,
        pd?.unitType,
        price
      );
      if (label) {
        this.setState({ selectedOption: label });
      }
      if (this.formRef) {
        const curGuestCount = this.formRef.getState().values?.guestCount;
        if (curGuestCount == null || String(curGuestCount).trim() === '') {
          this.formRef.change('guestCount', bookingSearchPrefill.guestCount);
        }
      }
    }
    if (bookingSearchPrefill?.startDateYmd && listingId && onFetchTimeSlots && timeZone) {
      const startDay = getStartOf(
        parseDateFromISO8601(bookingSearchPrefill.startDateYmd, '12:00', timeZone),
        'day',
        timeZone
      );
      const { rangeStart, rangeEnd } = monthTimeSlotsRange(startDay, timeZone);
      this.prefillMonthKeyRequested = `${listingId?.uuid || listingId}-${monthIdString(
        startDay,
        timeZone
      )}`;
      onFetchTimeSlots(listingId, rangeStart, rangeEnd, timeZone);
    }
  }

  applyPrefillAfterCommit() {
    this.tryApplyDateTimePrefill();
    this.applySearchPrefill();
  }

  tryApplyDateTimePrefill() {
    const { monthlyTimeSlots, intl, timeZone, listing } = this.props;
    const bookingSearchPrefill = this.getEffectivePrefill();
    if (this.prefillApplied || !this.formRef || !bookingSearchPrefill) {
      return false;
    }

    const minimumBookingHours = listing?.attributes?.publicData?.minimumBookingHours;
    const patch =
      monthlyTimeSlots && Object.keys(monthlyTimeSlots).length > 0
        ? buildBookingPrefillFormPatch(bookingSearchPrefill, {
            intl,
            timeZone,
            monthlyTimeSlots,
            minimumBookingHours,
          })
        : null;

    if (!patch?.bookingStartDate) {
      return false;
    }

    const currentValues = this.formRef.getState().values || {};
    const hasDateTimeValues = !!(currentValues.bookingStartTime && currentValues.bookingEndTime);

    if (!hasDateTimeValues) {
      this.formRef.batch(() => {
        this.formRef.change('bookingStartDate', patch.bookingStartDate);
        this.formRef.change('bookingStartTime', patch.bookingStartTime);
        this.formRef.change('bookingEndDate', patch.bookingEndDate);
        this.formRef.change('bookingEndTime', patch.bookingEndTime);
        if (patch.guestCount && !currentValues.guestCount) {
          this.formRef.change('guestCount', patch.guestCount);
        }
      });
    }

    const v = this.formRef.getState().values;
    if (v?.bookingStartTime && v?.bookingEndTime && v?.guestCount) {
      this.prefillApplied = true;
      this.handleOnChange({ values: v });
    }

    return true;
  }

  // Los espacios interiores también se pueden añadir desde su tarjeta en la ficha
  // (SectionIndoorSpacesMaybe): la página guarda la selección y aquí se lleva al formulario.
  // Al revés, handleOnChange avisa a la página cuando se marcan aquí.
  syncIndoorSpaceExtrasFromProps() {
    const { indoorSpaceExtras } = this.props;
    if (!this.formRef || !Array.isArray(indoorSpaceExtras)) {
      return;
    }
    const current = this.formRef.getState().values?.indoorSpaceExtras || [];
    if (current.join(',') !== indoorSpaceExtras.join(',')) {
      this.formRef.change('indoorSpaceExtras', indoorSpaceExtras);
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.indoorSpaceExtras !== this.props.indoorSpaceExtras) {
      this.syncIndoorSpaceExtrasFromProps();
    }
    const { listingId } = this.props;
    const bookingSearchPrefill = this.getEffectivePrefill();

    if (
      prevProps.fetchLineItemsInProgress &&
      !this.props.fetchLineItemsInProgress &&
      this.formRef
    ) {
      const currentValues = this.formRef.getState().values;
      if (currentValues) {
        this.handleOnChange({ values: currentValues });
      }
    }

    if (prevProps.listingId !== listingId) {
      this.prefillApplied = false;
      this.prefillMonthKeyRequested = null;
      this.lastLineItemsRequestKey = null;
    }
    const pk = bookingSearchPrefill ? JSON.stringify(bookingSearchPrefill) : '';
    const prevEffectivePrefill = this.getEffectivePrefill(prevProps);
    const prevPk = prevEffectivePrefill ? JSON.stringify(prevEffectivePrefill) : '';
    if (pk !== prevPk) {
      this.prefillApplied = false;
      this.prefillMonthKeyRequested = null;
      this.lastLineItemsRequestKey = null;
    }
  }

  applySearchPrefill() {
    const { monthlyTimeSlots, timeZone, intl, listing, price, listingId } = this.props;
    const bookingSearchPrefill = this.getEffectivePrefill();
    if (!bookingSearchPrefill || this.prefillApplied || !this.formRef) {
      return;
    }
    const needsDatePrefill =
      bookingSearchPrefill.startDateYmd && String(bookingSearchPrefill.startDateYmd).trim() !== '';

    // Even if ListingPage.loadData hasn't populated monthlyTimeSlots yet, we can still:
    // - apply guestCount immediately
    // - kick off the month fetch needed to prefill date/time
    const minimumBookingHours = listing?.attributes?.publicData?.minimumBookingHours;

    if (!monthlyTimeSlots || !Object.keys(monthlyTimeSlots).length) {
      this.applyGuestCountPrefillFromPatch(
        buildBookingPrefillFormPatch(bookingSearchPrefill, {
          intl,
          timeZone,
          monthlyTimeSlots: {},
          minimumBookingHours,
        }) || { guestCount: bookingSearchPrefill.guestCount }
      );
      if (needsDatePrefill) {
        const { onFetchTimeSlots } = this.props;
        if (onFetchTimeSlots && listingId && timeZone) {
          const startDay = getStartOf(
            parseDateFromISO8601(bookingSearchPrefill.startDateYmd, '12:00', timeZone),
            'day',
            timeZone
          );
          const monthKey = monthIdString(startDay, timeZone);
          const listingKey = listingId?.uuid || listingId;
          const prefillKey = `${listingKey}-${monthKey}`;
          if (this.prefillMonthKeyRequested !== prefillKey) {
            this.prefillMonthKeyRequested = prefillKey;
            const { rangeStart, rangeEnd } = monthTimeSlotsRange(startDay, timeZone);
            onFetchTimeSlots(listingId, rangeStart, rangeEnd, timeZone);
          }
        }
      }
      return;
    }

    const patch = buildBookingPrefillFormPatch(bookingSearchPrefill, {
      intl,
      timeZone,
      monthlyTimeSlots,
      minimumBookingHours,
    });
    if (!patch) {
      return;
    }

    const datePrefillInPatch = !!patch.bookingStartDate;

    if (needsDatePrefill && !datePrefillInPatch) {
      const startDay = getStartOf(
        parseDateFromISO8601(bookingSearchPrefill.startDateYmd, '12:00', timeZone),
        'day',
        timeZone
      );
      const monthKey = monthIdString(startDay, timeZone);
      const monthEntry = monthlyTimeSlots[monthKey];
      const listingKey = listingId?.uuid || listingId;
      const prefillKey = `${listingKey}-${monthKey}`;

      if (!monthEntry) {
        const { onFetchTimeSlots } = this.props;
        if (
          onFetchTimeSlots &&
          listingId &&
          timeZone &&
          this.prefillMonthKeyRequested !== prefillKey
        ) {
          this.prefillMonthKeyRequested = prefillKey;
          const { rangeStart, rangeEnd } = monthTimeSlotsRange(startDay, timeZone);
          onFetchTimeSlots(listingId, rangeStart, rangeEnd, timeZone);
        }
        this.applyGuestCountPrefillFromPatch(patch);
        return;
      }
      if (monthEntry.fetchTimeSlotsInProgress === true) {
        this.applyGuestCountPrefillFromPatch(patch);
        return;
      }
      // Month finished loading but we still have no date patch (e.g. empty slots for that
      // day, or data not yet usable). Never fall through to batch + prefillApplied — that
      // locks prefill forever while guest count alone was applied.
      this.applyGuestCountPrefillFromPatch(patch);
      return;
    }

    this.formRef.batch(() => {
      if (patch.guestCount) {
        this.formRef.change('guestCount', patch.guestCount);
      }
      if (patch.bookingStartDate) {
        this.formRef.change('bookingStartDate', patch.bookingStartDate);
        this.formRef.change('bookingStartTime', patch.bookingStartTime);
        this.formRef.change('bookingEndDate', patch.bookingEndDate);
        this.formRef.change('bookingEndTime', patch.bookingEndTime);
      }
    });

    const pd = listing?.attributes?.publicData;
    const label = patch.guestCount
      ? guestPackageDisplayLabel(
          facePriceRangeOf(this.props),
          patch.guestCount,
          intl,
          pd?.unitType,
          price
        )
      : null;
    if (label != null && label !== this.state.selectedOption) {
      this.setState({ selectedOption: label });
    }

    this.prefillApplied = true;

    const v = this.formRef.getState().values;
    if (v?.bookingStartTime && v?.bookingEndTime && v?.guestCount) {
      this.handleOnChange({ values: v });
    }
  }

  applyGuestCountPrefillFromPatch(patch) {
    const { intl, listing, price } = this.props;
    const pd = listing?.attributes?.publicData;
    if (!this.formRef) {
      return;
    }
    if (patch.guestCount) {
      const cur = this.formRef.getState().values?.guestCount;
      const hasCurrentGuestCount = cur != null && String(cur).trim() !== '';
      if (!hasCurrentGuestCount && cur !== patch.guestCount) {
        this.formRef.change('guestCount', patch.guestCount);
      }
    }
    const label = patch.guestCount
      ? guestPackageDisplayLabel(
          facePriceRangeOf(this.props),
          patch.guestCount,
          intl,
          pd?.unitType,
          price
        )
      : null;
    if (label != null && label !== this.state.selectedOption) {
      this.setState({ selectedOption: label });
    }
  }

  handleFormSubmit(e) {
    // La cara viaja con la reserva hasta el checkout: decide con qué tramos se cobra y
    // queda guardada en la transacción. Se resuelve aquí (fecha + ?espacio=) para no
    // depender de un re-render del padre tras cambiar el día.
    this.props.onSubmit({ ...e, bookingFace: this.resolveOrderFace(e) });
  }

  /**
   * Cara a cobrar/mostrar: `?espacio=` gana; si no, la fecha de inicio de la reserva.
   */
  resolveOrderFace(values = {}) {
    const { listing, faceParam, bookingFace, timeZone } = this.props;
    const startDate =
      (values.bookingStartTime ? timestampToDate(values.bookingStartTime) : null) ||
      values.bookingStartDate?.date ||
      null;
    const ymd = startDate && timeZone ? stringifyDateToISO8601(startDate, timeZone) : null;
    if (listing) {
      return resolveListingFace({ listing, faceParam, dateYmd: ymd });
    }
    return bookingFace;
  }

  stableTransactionLineItemsKey(listingId, orderData) {
    const id = listingId?.uuid ? listingId.uuid : String(listingId);
    const {
      bookingStart,
      bookingEnd,
      guestCount,
      firstBookingDiscount,
      extraItems,
      bookingFace,
      poolUseExtra,
      indoorSpaceExtras,
    } = orderData;
    const startMs =
      bookingStart &&
      typeof bookingStart.getTime === 'function' &&
      !Number.isNaN(bookingStart.getTime())
        ? bookingStart.getTime()
        : '';
    const endMs =
      bookingEnd && typeof bookingEnd.getTime === 'function' && !Number.isNaN(bookingEnd.getTime())
        ? bookingEnd.getTime()
        : '';
    let extras = '';
    try {
      extras = extraItems == null ? '' : JSON.stringify(extraItems);
    } catch (e) {
      extras = String(extraItems);
    }
    return `${id}|${startMs}|${endMs}|${guestCount}|${firstBookingDiscount}|${extras}|${bookingFace}|${!!poolUseExtra}|${(
      indoorSpaceExtras || []
    ).join(',')}`;
  }

  // When the values of the form are updated we need to fetch
  // lineItems from this template's backend for the EstimatedTransactionMaybe
  // In case you add more fields to the form, make sure you add
  // the values here to the orderData object.
  handleOnChange(formValues) {
    const values = formValues && formValues.values ? formValues.values : null;
    if (!values) {
      return;
    }
    const {
      bookingStartTime,
      bookingEndTime,
      extraItems,
      guestCount,
      poolUseExtra,
      indoorSpaceExtras,
      bookingStartDate,
    } = values;
    const startDate =
      (bookingStartTime ? timestampToDate(bookingStartTime) : null) ||
      bookingStartDate?.date ||
      null;
    const endDate = bookingEndTime ? timestampToDate(bookingEndTime) : null;

    const { onBookingDateChange, onIndoorSpaceExtrasChange, timeZone } = this.props;
    if (onIndoorSpaceExtrasChange) {
      const selected = Array.isArray(indoorSpaceExtras) ? indoorSpaceExtras : [];
      if (selected.join(',') !== (this.props.indoorSpaceExtras || []).join(',')) {
        onIndoorSpaceExtrasChange(selected);
      }
    }
    if (onBookingDateChange) {
      if (startDate && timeZone) {
        const ymd = stringifyDateToISO8601(startDate, timeZone);
        if (this.reportedBookingDateYmd !== ymd) {
          this.reportedBookingDateYmd = ymd;
          onBookingDateChange(ymd);
        }
      } else if (this.reportedBookingDateYmd != null) {
        this.reportedBookingDateYmd = null;
        onBookingDateChange(null);
      }
    }

    const listingId = this.props.listingId;
    const isOwnListing = this.props.isOwnListing;
    const pd = this.props.listing?.attributes?.publicData;
    const firstBookingDiscount = isFirstBookingDiscountFlagOn(pd?.firstBookingDiscountEnabled);
    // Note: we expect values bookingStartTime and bookingEndTime to be strings
    // which is the default case when the value has been selected through the form
    const isStartBeforeEnd = bookingStartTime < bookingEndTime;
    if (
      bookingStartTime &&
      bookingEndTime &&
      guestCount &&
      isStartBeforeEnd &&
      !this.props.fetchLineItemsInProgress
    ) {
      const orderData = {
        bookingStart: startDate,
        bookingEnd: endDate,
        extraItems: extraItems,
        guestCount,
        firstBookingDiscount,
        bookingFace: this.resolveOrderFace(values),
        poolUseExtra: poolUseExtra === true,
        indoorSpaceExtras: Array.isArray(indoorSpaceExtras) ? indoorSpaceExtras : [],
      };
      const dedupeKey = this.stableTransactionLineItemsKey(listingId, orderData);
      if (dedupeKey === this.lastLineItemsRequestKey) {
        return;
      }
      this.lastLineItemsRequestKey = dedupeKey;
      this.props.onFetchTransactionLineItems({
        orderData,
        listingId,
        isOwnListing,
      });
    }
  }

  // componentDidMount() {
  //   document.addEventListener('mousedown', this.handleClickOutside);
  // }

  // componentWillUnmount() {
  //   document.removeEventListener('mousedown', this.handleClickOutside);
  // }

  handleClickOutside = event => {
    if (this.myRef.current && !this.myRef.current.contains(event.target)) {
      this.setState({ showContent: false });
    }
  };

  handleButtonClick = () => {
    this.setState(prevState => ({
      showContent: !prevState.showContent,
    }));
  };

  handleOptionClick = (quantityRange, guestRange, form) => {
    this.prefillApplied = true;
    if (form && typeof form.change === 'function') {
      form.change('guestCount', guestRange);
    }
    this.setState({
      showContent: false,
      selectedOption: quantityRange,
    });
  };

  render() {
    const { selectedOption } = this.state;
    const {
      rootClassName,
      className,
      price: unitPrice,
      minBookingPriceEur,
      dayCountAvailableForBooking,
      marketplaceName,
      extraItems: extraItemsforSelect,
      user,
      location,
      ...rest
    } = this.props;

    const classes = classNames(rootClassName || css.root, className);

    return (
      <FinalForm
        {...rest}
        unitPrice={unitPrice}
        initialValues={this.getInitialValues()}
        onSubmit={this.handleFormSubmit}
        render={fieldRenderProps => {
          this.formRef = fieldRenderProps.form;
          const {
            endDatePlaceholder,
            startDatePlaceholder,
            form,
            pristine,
            handleSubmit,
            intl,
            isOwnListing,
            listingId,
            values,
            monthlyTimeSlots,
            onFetchTimeSlots,
            timeZone,
            lineItems,
            fetchLineItemsInProgress,
            fetchLineItemsError,
            listing,
            viewport,
            onSubmitInquiry,
            sendInquiryError,
            sendInquiryInProgress,
            onResendVerificationEmail,
            sendVerificationEmailInProgress,
            sendVerificationEmailError,
            isAuthenticated,
            history,
          } = fieldRenderProps;

          const [isOpen, setOpen] = useState(false);

          const { author, attributes } = listing || {};
          const { publicData } = attributes || {};
          const listingTitle = listing
            ? faceView(listing, this.resolveOrderFace(values)).title
            : attributes?.title;
          const { minimumBookingHours = 0, unitType } =
            (listing && listing?.id && publicData) || {};
          const priceRange = (listing && listing?.id && facePriceRangeOf(this.props)) || [];
          // El extra "uso de piscina" solo existe al reservar el jardín de un anuncio
          // que también es piscina. Su importe lo calcula el servidor (la diferencia
          // entre ambas caras), así que aquí solo se marca que se quiere.
          const showPoolUseExtra = offersPoolUseExtra(listing, this.resolveOrderFace(values));
          // Mismo sitio, caso contrario: jardín de un anuncio con piscina sin el extra.
          // Se avisa para que nadie reserve el jardín contando con la piscina.
          const showNoPoolUseWarning = gardenBookingExcludesPool(
            listing,
            this.resolveOrderFace(values)
          );
          // Espacios interiores que se pagan aparte (precio fijo por reserva). Igual que el
          // uso de piscina, aquí solo se marcan: el importe lo pone el servidor.
          const indoorSpaceExtras = extraIndoorSpacesOf(listing);
          const showFirstBookingDiscountBanner = isFirstBookingDiscountEligible(publicData, author);
          const authorDisplayName = author && author.id && author?.attributes?.profile?.displayName;
          const startTime = values && values.bookingStartTime ? values.bookingStartTime : null;
          const endTime = values && values.bookingEndTime ? values.bookingEndTime : null;
          const extraItems = values && values.extraItems ? values.extraItems : null;
          const startDate = startTime ? timestampToDate(startTime) : null;
          const endDate = endTime ? timestampToDate(endTime) : null;
          const totalGuests = values && values?.guestCount ? values?.guestCount : null;
          const hasBookingHours = parseInt(minimumBookingHours);
          const guestHoursBooking = lineItems && lineItems[0]?.quantity;
          const showSendMessageButton = typeof onSubmitInquiry == 'function';
          const selectPackageTitle = intl.formatMessage({
            id: 'BookingTimeForm.selectPackageTitle',
          });
          // Always re-derived from the current face's tiers: the stored `selectedOption`
          // bakes in the price seen when it was picked, so after a date change that flips
          // the face (pool ↔ garden) it would keep showing the other face's price. The
          // stored text is only the fallback when the tier can't be found.
          const selectedOptionFromValue = values?.guestCount
            ? guestPackageDisplayLabel(priceRange, values.guestCount, intl, unitType, unitPrice)
            : null;
          const isMobileLayout = viewport?.width < MAX_MOBILE_SCREEN_WIDTH;
          const disabledMessageButton =
            totalGuests == null ||
            endDate == null ||
            startTime == null ||
            startDate == null ||
            endTime == null;
          const lineItemTotal =
            Array.isArray(lineItems) && lineItems?.length > 0 && !disabledMessageButton
              ? lineItems?.reduce((acc, curr) => {
                  if (curr?.code != 'line-item/provider-commission') {
                    acc = acc + curr?.lineTotal?.amount;
                  } else {
                    acc += 0;
                  }
                  return acc;
                }, 0)
              : 0;

          const isBelowMinBookingPrice =
            minBookingPriceEur != null &&
            !disabledMessageButton &&
            Array.isArray(lineItems) &&
            lineItems.length > 0 &&
            isBookingBelowMinPrice(minBookingPriceEur, lineItems);
          const minAdvanceHours = parseMinAdvanceHours(publicData);
          const violatesMinAdvanceHours =
            startDate != null && isBookingBelowMinAdvanceHours(minAdvanceHours, startDate);
          const minBookingHoursNotMet =
            guestHoursBooking && hasBookingHours && guestHoursBooking < hasBookingHours;
          // Sin paquete de grupo el servidor no puede resolver el tramo de precio: caería al
          // último tramo de `priceRange` —el más caro— y la reserva se quedaría además sin los
          // line items `guest-count-*`, así que ni el anfitrión ni el desglose ven los bañistas.
          const guestPackageMissing = priceRange.length > 0 && totalGuests == null;
          const submitDisabled =
            guestPackageMissing ||
            minBookingHoursNotMet ||
            isBelowMinBookingPrice ||
            violatesMinAdvanceHours;

          const handleInquirySubmit = inquiryVal => {
            const { message } = inquiryVal || {};
            const updatedValues = {
              message: message,
              guestCount: totalGuests,
              bookingEndDate: values?.bookingEndDate,
              bookingEndTime: endTime,
              bookingStartDate: values?.bookingStartDate,
              bookingStartTime: startTime,
              extraItems: extraItems,
              // Igual que al reservar: la consulta guarda la cara para mostrar su título.
              bookingFace: this.resolveOrderFace(values),
              // Y la casilla "uso de piscina": sin ella el desglose de la consulta no la cobra.
              poolUseExtra: values?.poolUseExtra === true,
            };
            onSubmitInquiry(updatedValues);
          };

          const handleModalOpenInquiry = () => {
            if (!isAuthenticated) {
              openAccessLoginModal(history, location, { accessContext: 'booking' });
            } else if (isAuthenticated && isOwnListing) {
              typeof window != 'undefined' && window && window.scroll(0, 0);
            } else if (isAuthenticated && !isOwnListing) {
              setOpen(true);
            }
          };

          // This is the place to collect breakdown estimation data. See the
          // EstimatedCustomerBreakdownMaybe component to change the calculations
          // for customized payment processes.
          const breakdownData =
            startDate && endDate
              ? {
                  startDate,
                  endDate,
                  extraItems,
                  guestCountRange: totalGuests,
                }
              : null;
          const showEstimatedBreakdown =
            breakdownData && lineItems && !fetchLineItemsInProgress && !fetchLineItemsError;

          return (
            <Form onSubmit={handleSubmit} className={classes} enforcePagePreloadFor="CheckoutPage">
              <FormSpy
                subscription={{ values: true }}
                onChange={values => {
                  this.handleOnChange(values);
                }}
              />

              <div className={css.guestCount}>
                {showFirstBookingDiscountBanner ? (
                  <p className={css.firstBookingDiscountBanner} role="status">
                    <FormattedMessage id="BookingTimeForm.firstBookingDiscountBanner" />
                  </p>
                ) : null}
                <label className={css.guestLabel}>
                  <FormattedMessage id="BookingTimeForm.guestCountTitle" />
                </label>
                <div className={css.notCount}>
                  <FormattedMessage id="BookingTimeForm.babiesnotCountTitle" />
                </div>
                {/* Custom Drop-Down Code */}
                <div className={css.guestDetail}>
                  <div className={css.guestDropdown} onClick={this.handleButtonClick}>
                    {selectedOptionFromValue ||
                      (selectedOption ? `${selectedOption}` : selectPackageTitle)}
                  </div>
                  {this.state.showContent && (
                    <ul className={css.guestOpenDropdown} ref={this.myRef}>
                      {Array.isArray(priceRange) &&
                        priceRange.map((ele, index) => {
                          let displayPriceItem = '';
                          let quantityText = '';
                          if (isGuestTier51PlusKey(ele.quantityRange)) {
                            quantityText = intl.formatMessage({
                              id: 'ListingReservationBreakdown.guests51Plus',
                            });
                          } else if (ele.quantityRange.split('-').length > 1) {
                            quantityText = intl.formatMessage(
                              { id: 'ListingReservationBreakdown.guestsBetween' },
                              {
                                min: ele.quantityRange.split('-')[0],
                                max: ele.quantityRange.split('-')[1],
                              }
                            );
                          } else {
                            quantityText = intl.formatMessage(
                              { id: 'ListingReservationBreakdown.guestsExact' },
                              { n: ele.quantityRange }
                            );
                          }
                          const displayAmount =
                            unitType === 'hour'
                              ? applyHourlyCustomerPriceSubunits(ele.amount)
                              : ele.amount;
                          const displayMoney = new Money(displayAmount, ele.currency);
                          displayPriceItem = `${quantityText} ${formatMoney(
                            intl,
                            displayMoney
                          )} /h`;
                          return (
                            <li
                              className={css.dropdownList}
                              key={index}
                              onClick={() =>
                                this.handleOptionClick(displayPriceItem, ele.quantityRange, form)
                              }
                            >
                              <div className={css.quantityText}>{quantityText}</div>
                              <div className={css.price}>{formatMoney(intl, displayMoney)} /h</div>
                            </li>
                          );
                        })}
                    </ul>
                  )}
                </div>
              </div>

              {monthlyTimeSlots && timeZone ? (
                <FieldDateAndTimeInput
                  className={css.inputBox}
                  startDateInputProps={{
                    label: intl.formatMessage({ id: 'BookingTimeForm.bookingStartTitle' }),
                    placeholderText: startDatePlaceholder,
                  }}
                  endDateInputProps={{
                    label: intl.formatMessage({ id: 'BookingTimeForm.bookingEndTitle' }),
                    placeholderText: endDatePlaceholder,
                  }}
                  listingId={listingId}
                  onFetchTimeSlots={onFetchTimeSlots}
                  monthlyTimeSlots={monthlyTimeSlots}
                  values={values}
                  intl={intl}
                  form={form}
                  pristine={pristine}
                  timeZone={timeZone}
                  dayCountAvailableForBooking={365}
                  disabledUntilNoGuest={false}
                  minimumBookingHours={minimumBookingHours}
                />
              ) : null}
              <AfterFieldsSubscribed onCommit={this.applyPrefillAfterCommit} />

              {guestPackageMissing && startDate && endDate ? (
                <p className={css.finePrint}>
                  <H6 className={css.errorHeading}>
                    <FormattedMessage id="BookingTimeForm.guestPackageRequired" />
                  </H6>
                </p>
              ) : null}

              {minBookingHoursNotMet ? (
                <p className={css.finePrint}>
                  <H6 className={css.errorHeading}>
                    <FormattedMessage
                      id="bookingTimeForm.minimumNumberBookingHoursError"
                      values={{ hasBookingHours: hasBookingHours }}
                    />
                  </H6>
                </p>
              ) : null}

              {violatesMinAdvanceHours ? (
                <p className={css.finePrint}>
                  <H6 className={css.errorHeading}>
                    <FormattedMessage
                      id="BookingTimeForm.minAdvanceHoursError"
                      values={{ hours: minAdvanceHours }}
                    />
                  </H6>
                </p>
              ) : null}

              {isBelowMinBookingPrice ? (
                <p className={css.finePrint}>
                  <H6 className={css.errorHeading}>
                    <FormattedMessage
                      id="BookingTimeForm.minBookingPriceError"
                      values={{ minPrice: Math.round(minBookingPriceEur) }}
                    />
                  </H6>
                </p>
              ) : null}

              {showPoolUseExtra ? (
                <div className={css.extraBox}>
                  <label className={css.guestLabel}>
                    <FormattedMessage id="BookingTimeForm.poolUseExtraLabel" />
                  </label>
                  <FieldCheckbox
                    className={css.extraItem}
                    id="poolUseExtra"
                    name="poolUseExtra"
                    label={<FormattedMessage id="BookingTimeForm.poolUseExtraOption" />}
                  />
                </div>
              ) : showNoPoolUseWarning ? (
                <div className={css.extraBox}>
                  <label className={css.guestLabel}>
                    <FormattedMessage id="BookingTimeForm.poolUseExtraLabel" />
                  </label>
                  <p className={css.noPoolUseWarning} role="note">
                    <FormattedMessage id="BookingTimeForm.noPoolUseWarning" />
                  </p>
                </div>
              ) : null}

              {indoorSpaceExtras.length > 0 ? (
                <div className={css.extraBox}>
                  <label className={css.guestLabel}>
                    <FormattedMessage id="BookingTimeForm.indoorSpaceExtrasLabel" />
                  </label>
                  {indoorSpaceExtras.map(space => (
                    <FieldCheckbox
                      key={space.id}
                      className={css.extraItem}
                      id={`indoorSpaceExtra-${space.id}`}
                      name="indoorSpaceExtras"
                      value={space.id}
                      label={
                        <div>
                          <span>{space.title}</span>
                          <span>
                            {formatMoney(
                              intl,
                              new Money(
                                unitType === 'hour'
                                  ? applyHourlyCustomerPriceSubunits(space.price.amount)
                                  : space.price.amount,
                                space.price.currency
                              )
                            )}
                          </span>
                        </div>
                      }
                    />
                  ))}
                </div>
              ) : null}

              {/* Extra Items */}
              <div className={css.extraBox}>
                {extraItemsforSelect && extraItemsforSelect.length ? (
                  <div>
                    <label className={css.guestLabel}>
                      <FormattedMessage id="bookingTimeForm.extraItemsLabel" />
                    </label>
                    {extraItemsforSelect.map((item, index) => {
                      const extraAmount =
                        unitType === 'hour'
                          ? applyHourlyCustomerPriceSubunits(item.itemPrice.amount)
                          : item.itemPrice.amount;
                      const itemPrice = new Money(extraAmount, item.itemPrice.currency);
                      return (
                        <FieldCheckbox
                          className={css.extraItem}
                          key={`extraItem-id${index}`}
                          id={`extraItem-id${index}`}
                          name={`extraItems`}
                          label={
                            <div>
                              <span>{item.itemName}</span>
                              <span>{formatMoney(intl, itemPrice)}</span>
                            </div>
                          }
                          // class for item price "itemPrice"
                          value={item}
                        />
                      );
                    })}
                  </div>
                ) : null}
              </div>

              {showEstimatedBreakdown && !isMobileLayout ? (
                <div className={css.priceBreakdownContainer}>
                  <H6 as="h3" className={css.bookingBreakdownTitle}>
                    <FormattedMessage id="BookingTimeForm.priceBreakdownTitle" />
                  </H6>
                  {unitType === 'hour' ? (
                    <ListingReservationBreakdown
                      lineItems={lineItems}
                      listing={listing}
                      currency={unitPrice.currency}
                      marketplaceName={marketplaceName}
                    />
                  ) : (
                    <EstimatedCustomerBreakdownMaybe
                      breakdownData={breakdownData}
                      lineItems={lineItems}
                      timeZone={timeZone}
                      currency={unitPrice.currency}
                      marketplaceName={marketplaceName}
                      processName={BOOKING_PROCESS_NAME}
                    />
                  )}
                </div>
              ) : null}

              {showSendMessageButton && isMobileLayout && !disabledMessageButton ? (
                <div className={css.sendMessageButton}>
                  <span className={css.defaultTextMessage}>
                    <FormattedMessage id="BookingTimeForm.messagefirstText" />
                  </span>
                  <span className={css.messageLink} onClick={handleModalOpenInquiry}>
                    <FormattedMessage id="BookingTimeForm.messageSecondTextLink" />
                  </span>
                </div>
              ) : null}

              {fetchLineItemsError ? (
                <span className={css.sideBarError}>
                  <FormattedMessage id="BookingTimeForm.fetchLineItemsError" />
                </span>
              ) : null}

              <div className={css.modalButton}>
                {isMobileLayout ? (
                  <div className={css.priceButton}>
                    {formatMoney(intl, new Money(lineItemTotal, 'EUR'))}
                  </div>
                ) : null}
                <div className={css.submitButton}>
                  <PrimaryButton
                    type="submit"
                    inProgress={fetchLineItemsInProgress}
                    disabled={submitDisabled}
                  >
                    <FormattedMessage id="BookingTimeForm.requestToBook" />
                  </PrimaryButton>
                </div>
                {showSendMessageButton && !isMobileLayout ? (
                  <div className={css.sendMessageButton}>
                    <SecondaryButton
                      type="button"
                      onClick={handleModalOpenInquiry}
                      disabled={disabledMessageButton}
                    >
                      <FormattedMessage id="BookingTimeForm.ContactButton" />
                    </SecondaryButton>
                  </div>
                ) : null}
                <p className={classNames(css.finePrint, css.finePrintAfterPrimary)}>
                  <FormattedMessage
                    id={
                      isOwnListing
                        ? 'BookingTimeForm.ownListing'
                        : 'BookingTimeForm.youWontBeChargedInfo'
                    }
                  />
                </p>
              </div>

              <Modal
                id="ListingPage.inquiry"
                isOpen={isOpen}
                contentClassName={css.inquiryModalContent}
                onClose={() => {
                  setOpen(false);
                }}
                onManageDisableScrolling={() => {}}
              >
                <InquiryForm
                  className={css.inquiryForm}
                  submitButtonWrapperClassName={css.inquirySubmitButtonWrapper}
                  listingTitle={listingTitle}
                  authorDisplayName={authorDisplayName}
                  sendInquiryError={sendInquiryError}
                  onSubmit={handleInquirySubmit}
                  inProgress={sendInquiryInProgress}
                  onResendVerificationEmail={onResendVerificationEmail}
                  sendVerificationEmailInProgress={sendVerificationEmailInProgress}
                  sendVerificationEmailError={sendVerificationEmailError}
                />
              </Modal>
            </Form>
          );
        }}
      />
    );
  }
}

BookingTimeFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  price: null,
  isOwnListing: false,
  listingId: null,
  startDatePlaceholder: null,
  endDatePlaceholder: null,
  monthlyTimeSlots: null,
  lineItems: null,
  fetchLineItemsError: null,
  bookingSearchPrefill: null,
  bookingInitialPrefill: null,
  bookingFace: null,
  faceParam: null,
  onBookingDateChange: null,
  indoorSpaceExtras: null,
  onIndoorSpaceExtrasChange: null,
};

BookingTimeFormComponent.propTypes = {
  rootClassName: string,
  className: string,

  marketplaceName: string.isRequired,
  price: propTypes.money,
  isOwnListing: bool,
  listingId: propTypes.uuid,
  monthlyTimeSlots: object,
  onFetchTimeSlots: func.isRequired,
  timeZone: string.isRequired,
  bookingFace: string,
  faceParam: string,
  facePriceRange: array,

  onFetchTransactionLineItems: func.isRequired,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,

  // from injectIntl
  intl: intlShape.isRequired,

  // for tests
  startDatePlaceholder: string,
  endDatePlaceholder: string,

  dayCountAvailableForBooking: number.isRequired,
  bookingSearchPrefill: object,
  bookingInitialPrefill: object,
  onBookingDateChange: func,
  indoorSpaceExtras: arrayOf(string),
  onIndoorSpaceExtrasChange: func,
  location: shape({
    pathname: string.isRequired,
    search: string,
    hash: string,
    state: object,
  }).isRequired,
};

const BookingTimeForm = compose(injectIntl)(BookingTimeFormComponent);
BookingTimeForm.displayName = 'BookingTimeForm';

export default BookingTimeForm;
